import { useState } from "react";
import { useLocation, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet";
import { Loader2, ChevronLeft, Download, ArrowLeft } from "lucide-react";
import { DashboardHeader } from "@/components/dashboard/dashboard-header";
import { DashboardShell } from "@/components/dashboard/dashboard-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";
import { useSelectedBusiness } from "@/hooks/use-selected-business";

export default function ViewLegalDocumentPage() {
  const [location] = useLocation();
  const { toast } = useToast();
  const { activeBusiness: selectedBusiness } = useSelectedBusiness();
  
  // Extract document ID from URL
  const documentId = parseInt(decodeURIComponent(location.split('/legal/view/')[1] || ''));
  const businessId = selectedBusiness?.id;

  const {
    data: document,
    isLoading: isDocumentLoading,
    error
  } = useQuery({
    queryKey: ["/api/businesses", businessId, "legal-documents", documentId],
    queryFn: async () => {
      if (!businessId) throw new Error("Business not selected");
      const res = await apiRequest("GET", `/api/businesses/${businessId}/legal-documents/${documentId}`);
      return await res.json();
    },
    enabled: !!documentId && !isNaN(documentId) && !!businessId
  });

  const handleDownload = () => {
    if (document && document.documentFileId && businessId) {
      window.open(`/api/businesses/${businessId}/legal-documents/${documentId}/download`, '_blank');
    } else {
      toast({
        title: "Download Unavailable",
        description: "The PDF version of this document is not available yet.",
        variant: "destructive"
      });
    }
  };

  if (isDocumentLoading) {
    return (
      <DashboardShell>
        <DashboardHeader heading="Legal Document" text="View your generated legal document" />
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardShell>
    );
  }

  if (error) {
    return (
      <DashboardShell>
        <DashboardHeader heading="Legal Document" text="View your generated legal document" />
        <div className="rounded-lg border border-destructive bg-destructive/10 p-8 text-center">
          <h2 className="text-xl font-semibold mb-2 text-destructive">Error Loading Document</h2>
          <p className="text-muted-foreground mb-4">
            There was a problem loading the document. It may have been deleted or you don't have permission to view it.
          </p>
          <Button variant="outline" asChild>
            <Link href="/legal">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Documents
            </Link>
          </Button>
        </div>
      </DashboardShell>
    );
  }

  if (!document) {
    return (
      <DashboardShell>
        <DashboardHeader heading="Legal Document" text="View your generated legal document" />
        <div className="rounded-lg border p-8 text-center">
          <h2 className="text-xl font-semibold mb-2">Document Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The document you are looking for could not be found.
          </p>
          <Button variant="outline" asChild>
            <Link href="/legal">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Documents
            </Link>
          </Button>
        </div>
      </DashboardShell>
    );
  }

  // Format the document content for display
  const formatContent = (content: string) => {
    return content.split('\n').map((line, index) => (
      <p key={index} className={line.trim().length === 0 ? "h-4" : "mb-2"}>
        {line}
      </p>
    ));
  };

  return (
    <DashboardShell>
      <Helmet>
        <title>{document.documentName} | ZentroBooks</title>
        <meta name="description" content="View your generated legal document" />
      </Helmet>
      
      <DashboardHeader 
        heading={document.documentName} 
        text="View your generated legal document" 
      >
        <div className="flex space-x-2">
          <Button variant="outline" asChild>
            <Link href="/legal">
              <ChevronLeft className="mr-2 h-4 w-4" />
              Back
            </Link>
          </Button>
          <Button onClick={handleDownload} disabled={!document.documentFileId}>
            <Download className="mr-2 h-4 w-4" />
            Download PDF
          </Button>
        </div>
      </DashboardHeader>

      {/* Document metadata */}
      <div className="flex flex-wrap gap-2 mb-4 text-sm text-muted-foreground">
        <div>Created: {format(new Date(document.createdAt), "MMMM d, yyyy")}</div>
        <div>·</div>
        <div>Template: {document.template?.name || "Custom"}</div>
      </div>

      {/* Document content */}
      <Card>
        <CardContent className="p-6">
          <div className="prose max-w-none">
            {formatContent(document.content)}
          </div>
        </CardContent>
      </Card>
    </DashboardShell>
  );
}
